import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LogService } from 'shared';

import { BiometricService } from './biometric.service';
import { CredentialVaultService } from './credential-vault.service';
import { generateSalt, hashPin, verifyPin } from './pin-crypto.utils';

export interface LockoutInfo {
  isLocked: boolean;
  remainingSeconds: number;
  attemptsRemaining: number;
}

// Progressive lockout thresholds
const LOCKOUT_THRESHOLDS = [
  { attempts: 5, durationMs: 30_000 },
  { attempts: 8, durationMs: 300_000 },
  { attempts: 11, durationMs: -1 }, // -1 = wipe vault
] as const;

const MAX_ATTEMPTS = 11;

@Injectable({
  providedIn: 'root',
})
export class LocalAuthService {
  private readonly vault = inject(CredentialVaultService);
  private readonly biometricService = inject(BiometricService);
  private readonly router = inject(Router);
  private readonly logger = inject(LogService);

  // ── PIN Setup ──

  async setupPin(pin: string): Promise<boolean> {
    try {
      const salt = generateSalt();
      const hash = await hashPin(pin, salt);
      await this.vault.savePinHash(hash, salt);
      await this.vault.clearLockoutState();
      return true;
    } catch (error) {
      this.logger.error('Failed to setup PIN', 'LocalAuthService.setupPin', error);
      return false;
    }
  }

  // ── PIN Verification ──

  async verifyPin(pin: string): Promise<boolean> {
    const lockoutInfo = await this.checkLockout();
    if (lockoutInfo.isLocked) return false;

    const pinData = await this.vault.getPinHash();
    if (!pinData) {
      this.logger.error('No PIN hash found', 'LocalAuthService.verifyPin');
      return false;
    }

    const isValid = await verifyPin(pin, pinData.hash, pinData.salt);

    if (isValid) {
      await this.vault.clearLockoutState();
      return true;
    }

    // Increment failure count
    await this.handleFailedAttempt();
    return false;
  }

  // ── Change PIN (atomic swap) ──

  async changePin(currentPin: string, newPin: string): Promise<boolean> {
    // Step 1: Verify current PIN
    const pinData = await this.vault.getPinHash();
    if (!pinData) return false;

    const isCurrentValid = await verifyPin(currentPin, pinData.hash, pinData.salt);
    if (!isCurrentValid) return false;

    // Step 2: Hash new PIN
    const newSalt = generateSalt();
    const newHash = await hashPin(newPin, newSalt);

    // Step 3: Write new hash (savePinHash does read-back verification)
    try {
      await this.vault.savePinHash(newHash, newSalt);
      return true;
    } catch (error) {
      this.logger.error('Change PIN failed — old PIN remains valid', 'LocalAuthService', error);
      return false;
    }
  }

  async changePinWithBiometric(newPin: string): Promise<boolean> {
    const biometricSuccess = await this.biometricService.authenticate(
      'Authenticate to change your PIN',
    );
    if (!biometricSuccess) return false;

    const newSalt = generateSalt();
    const newHash = await hashPin(newPin, newSalt);

    try {
      await this.vault.savePinHash(newHash, newSalt);
      return true;
    } catch (error) {
      this.logger.error('Change PIN with biometric failed', 'LocalAuthService', error);
      return false;
    }
  }

  // ── Biometric Unlock (from PIN screen) ──

  async unlockWithBiometric(): Promise<boolean> {
    const biometricEnabled = await this.vault.isBiometricEnabled();
    if (!biometricEnabled) return false;

    return this.biometricService.authenticate('Unlock DreamPay');
  }

  // ── Biometric Login (cold start — session restoration) ──

  async authenticateFromLogin(): Promise<boolean> {
    const biometricEnabled = await this.vault.isBiometricEnabled();
    if (!biometricEnabled) return false;

    const success = await this.biometricService.authenticate('Sign in to DreamPay');
    if (!success) return false;

    // Verify we have valid tokens
    const token = await this.vault.getToken();
    return !!token?.accessToken;
  }

  // ── Token Refresh After Unlock ──

  async refreshTokenIfNeeded(): Promise<boolean> {
    const isExpired = await this.vault.isAccessTokenExpired();
    if (!isExpired) return true;

    // Token is expired — the next API call will trigger the interceptor's 401 handler.
    // We check if we have a refresh token to determine if re-login is needed.
    const token = await this.vault.getToken();
    if (!token?.refreshToken) {
      void this.router.navigate(['/auth/session-expired']);
      return false;
    }

    // Let the normal API flow handle refresh via the interceptor.
    return true;
  }

  // ── Lockout Logic ──

  async checkLockout(): Promise<LockoutInfo> {
    const { attemptCount, lockoutExpiry } = await this.vault.getLockoutState();

    if (lockoutExpiry > 0 && Date.now() < lockoutExpiry) {
      const remainingSeconds = Math.ceil((lockoutExpiry - Date.now()) / 1000);
      return {
        isLocked: true,
        remainingSeconds,
        attemptsRemaining: MAX_ATTEMPTS - attemptCount,
      };
    }

    return {
      isLocked: false,
      remainingSeconds: 0,
      attemptsRemaining: MAX_ATTEMPTS - attemptCount,
    };
  }

  private async handleFailedAttempt(): Promise<void> {
    const { attemptCount } = await this.vault.getLockoutState();
    const newCount = attemptCount + 1;

    // Check thresholds
    for (const threshold of LOCKOUT_THRESHOLDS) {
      if (newCount === threshold.attempts) {
        if (threshold.durationMs === -1) {
          // Max lockout — wipe vault and force re-login
          this.logger.warn('Max PIN attempts reached — wiping vault', 'LocalAuthService');
          await this.vault.clearAll();
          // Keep lockout state so brute-force counter persists
          await this.vault.setLockoutState(newCount, 0);
          void this.router.navigate(['/auth/session-expired']);
          return;
        }

        const expiry = Date.now() + threshold.durationMs;
        await this.vault.setLockoutState(newCount, expiry);
        return;
      }
    }

    // No threshold hit — just increment counter
    await this.vault.setLockoutState(newCount, 0);
  }

  // ── PIN State Queries ──

  async isPinConfigured(): Promise<boolean> {
    return this.vault.isPinConfigured();
  }

  // ── Forgot PIN ──

  async forgotPin(): Promise<void> {
    await this.vault.clearAll();
    void this.router.navigate(['/auth/login'], { replaceUrl: true });
  }
}
